/**
 * Add a candidate — `POST /recruiting/candidates`, WP-W2.
 *
 * Only `full_name` is required (the server's own `required: [full_name]`), so the
 * submit is disabled while it is empty — a true sentence about the operator ("you have
 * not named the person"), not about permission, and the shape that keeps a `400` from
 * being how a mandatory field is learned.
 *
 * Every other field is optional and **omitted when blank** rather than sent empty: an
 * absent key and a blank string are different requests, and `RecruitingCandidateCreateRequest`
 * declares these present-or-absent. Consent is a checkbox that, when ticked, sends the
 * **current instant** as `privacy_consent_at` — a consent record needs a time, and the
 * honest one is now; left unticked, the key is absent.
 *
 * A duplicate email against a non-redacted candidate answers `409 WORKFORCE_CONFLICT`,
 * shown as the server's own sentence — "published" and "which row" are facts this form
 * does not pre-judge.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type {
  CandidateSource,
  RecruitingCandidate,
  RecruitingCandidateCreateRequest,
} from '../../api/recruiting';
import { CANDIDATE_SOURCES } from '../../api/recruiting';
import { SOURCE_LABEL } from '../../recruiting/candidate';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const NAME_MAX = 500;

export interface CreateCandidateFormProps {
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly created: RecruitingCandidate | null;
  readonly onSubmit: (body: RecruitingCandidateCreateRequest) => void;
}

export function CreateCandidateForm({
  pending,
  error,
  created,
  onSubmit,
}: CreateCandidateFormProps): ReactNode {
  const { t } = useI18n();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState<'' | CandidateSource>('');
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentCompany, setCurrentCompany] = useState('');
  const [location, setLocation] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [consent, setConsent] = useState(false);

  const ready = fullName.trim().length > 0;

  const build = (): RecruitingCandidateCreateRequest => {
    const email_ = email.trim();
    const phone_ = phone.trim();
    const title_ = currentTitle.trim();
    const company_ = currentCompany.trim();
    const location_ = location.trim();
    const linkedin_ = linkedin.trim();
    return {
      full_name: fullName.trim(),
      ...(email_ === '' ? {} : { email: email_ }),
      ...(phone_ === '' ? {} : { phone: phone_ }),
      ...(source === '' ? {} : { source }),
      ...(title_ === '' ? {} : { current_title: title_ }),
      ...(company_ === '' ? {} : { current_company: company_ }),
      ...(location_ === '' ? {} : { location: location_ }),
      ...(linkedin_ === '' ? {} : { linkedin_url: linkedin_ }),
      // A consent record needs a time; the honest one is the instant it was recorded.
      ...(consent ? { privacy_consent_at: new Date().toISOString() } : {}),
    };
  };

  return (
    <section className="wp-section" aria-labelledby="w2-create-heading" data-testid="w2-create">
      <h2 className="wp-section__title" id="w2-create-heading">
        {t('recruiting.candidates.create.title')}
      </h2>
      <p className="wp-small wp-muted">{t('recruiting.candidates.create.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (!ready || pending) return;
          onSubmit(build());
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-name">
            {t('recruiting.candidates.create.fullName')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-name"
            data-testid="w2-create-name"
            type="text"
            value={fullName}
            maxLength={NAME_MAX}
            autoComplete="off"
            onChange={(event) => {
              setFullName(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('recruiting.candidates.create.fullNameRule')}</p>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-email">
            {t('recruiting.candidates.create.email')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-email"
            data-testid="w2-create-email"
            type="email"
            value={email}
            autoComplete="off"
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-phone">
            {t('recruiting.candidates.create.phone')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-phone"
            data-testid="w2-create-phone"
            type="text"
            value={phone}
            autoComplete="off"
            onChange={(event) => {
              setPhone(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-source">
            {t('recruiting.candidates.create.source')}
          </label>
          <select
            className="wp-field__input"
            id="w2-create-source"
            data-testid="w2-create-source"
            value={source}
            onChange={(event) => {
              setSource(event.target.value as '' | CandidateSource);
            }}
          >
            <option value="">{t('recruiting.candidates.source.none')}</option>
            {CANDIDATE_SOURCES.map((value) => (
              <option key={value} value={value}>
                {t(SOURCE_LABEL[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-title">
            {t('recruiting.candidates.create.currentTitle')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-title"
            data-testid="w2-create-title"
            type="text"
            value={currentTitle}
            autoComplete="off"
            onChange={(event) => {
              setCurrentTitle(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-company">
            {t('recruiting.candidates.create.currentCompany')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-company"
            data-testid="w2-create-company"
            type="text"
            value={currentCompany}
            autoComplete="off"
            onChange={(event) => {
              setCurrentCompany(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-location">
            {t('recruiting.candidates.create.location')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-location"
            data-testid="w2-create-location"
            type="text"
            value={location}
            autoComplete="off"
            onChange={(event) => {
              setLocation(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w2-create-linkedin">
            {t('recruiting.candidates.create.linkedin')}
          </label>
          <input
            className="wp-field__input"
            id="w2-create-linkedin"
            data-testid="w2-create-linkedin"
            type="url"
            value={linkedin}
            autoComplete="off"
            onChange={(event) => {
              setLinkedin(event.target.value);
            }}
          />
        </div>

        <div className="wp-filters__group">
          <label className="wp-toggle" htmlFor="w2-create-consent">
            <input
              id="w2-create-consent"
              type="checkbox"
              data-testid="w2-create-consent"
              checked={consent}
              onChange={(event) => {
                setConsent(event.target.checked);
              }}
            />
            <span>{t('recruiting.candidates.create.consent')}</span>
          </label>
        </div>

        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w2-create-submit"
            disabled={!ready || pending}
          >
            {pending
              ? t('recruiting.candidates.create.submitting')
              : t('recruiting.candidates.create.submit')}
          </button>
        </div>
      </form>

      {error === null ? null : (
        <ApiErrorNote
          error={error}
          subject={t('recruiting.candidates.subjectWord')}
          testId="w2-create-error"
        />
      )}
      {created === null ? null : (
        <StatusNote
          severity="low"
          title={t('recruiting.candidates.create.ok', { name: created.full_name })}
          testId="w2-create-ok"
        />
      )}
    </section>
  );
}
